import React, { useState, useRef } from 'react';
import './Contact.css';
import { useLanguage } from '../../../../context/LanguageContext';
import translations from '../../../../translations';
import { FaCheckCircle, FaTimes } from 'react-icons/fa';

const AttachIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="currentColor">
    <path d="M330-240q-104 0-177-73T80-490q0-104 73-177t177-73h370q75 0 127.5 52.5T880-560q0 75-52.5 127.5T700-380H350q-46 0-78-32t-32-78q0-46 32-78t78-32h370v80H350q-13 0-21.5 8.5T320-490q0 13 8.5 21.5T350-460h350q42-1 71-29.5t29-70.5q0-42-29-71t-71-29H330q-71-1-120.5 49T160-490q0 70 49.5 119T330-320h390v80H330Z"/>
  </svg>
);

const ALLOWED_TYPES = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/svg+xml',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];
const MAX_FILE_SIZE_MB = 20;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_FILES = 5;

const Contact = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [attachedFiles, setAttachedFiles] = useState([]);
  const [fileErrors, setFileErrors] = useState([]);

  const [formStatus, setFormStatus] = useState({
    status: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files);
    const errors = [];
    const valid = [];

    selected.forEach(file => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        errors.push(`"${file.name}" ${t.fileInvalidType}`);
      } else if (file.size > MAX_FILE_SIZE_BYTES) {
        errors.push(`"${file.name}" ${t.fileTooLarge}`);
      } else {
        valid.push(file);
      }
    });

    setFileErrors(errors);
    setAttachedFiles(prev => {
      const combined = [...prev, ...valid];
      return combined.slice(0, MAX_FILES);
    });

    // Reset input so the same file can be re-added after removal
    e.target.value = '';
  };

  const removeFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      setFormStatus({
        status: 'error',
        message: 'Please fill in all required fields'
      });
      return;
    }

    setIsSubmitting(true);

    const payload = new FormData();
    payload.append('name', formData.name);
    payload.append('email', formData.email);
    payload.append('phone', formData.phone);
    payload.append('message', formData.message);
    attachedFiles.forEach(file => payload.append('attachments[]', file));

    fetch('/contact.php', {
      method: 'POST',
      body: payload
    })
      .then(async response => {
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || 'Network response was not ok');
        }
        return response.json();
      })
      .then(() => {
        setFormStatus({
          status: 'success',
          message: 'Thank you for your message. We will get back to you soon!'
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
        setAttachedFiles([]);
        setFileErrors([]);
      })
      .catch(error => {
        console.error('Form submission error:', error);
        setFormStatus({
          status: 'error',
          message: 'Something went wrong. Please try again later.'
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const SuccessMessage = () => (
    <div className={`contact__success${language === 'ar' ? ' contact__rtl' : ''}`}>
      <FaCheckCircle className="contact__success-icon" />
      <h3 className="contact__success-title">{t.thankYou || 'Thank You!'}</h3>
      <p className="contact__success-message">
        {t.contactSuccessMessage || 'We have received your message and will get back to you as soon as possible.'}
      </p>
    </div>
  );

  return (
    <section className="contact" id="contact">
      <div className="container">
        <div className="contact__content">
          {formStatus.status === 'success' ? (
            <SuccessMessage />
          ) : (
            <>

              <div className="contact__header">
                <h2 className="contact__title">{t.contactTitle}</h2>
                <p className="contact__subtitle">
                  {t.contactSubtitle}
                </p>
                <div className="contact__divider"></div>
              </div>

              <div className="contact__form-container">
                <form className={`contact__form${language === 'ar' ? ' contact__rtl' : ''}`} onSubmit={handleSubmit}>
                  {formStatus.status && formStatus.status !== 'success' && (
                    <div className={`contact__alert contact__alert--${formStatus.status}`}>
                      {formStatus.message}
                    </div>
                  )}

                  <div className="contact__form-group">
                    <label htmlFor="name" className="contact__label">{t.nameLabel}</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="contact__input"
                      placeholder={t.namePlaceholder}
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="contact__form-group">
                    <label htmlFor="email" className="contact__label">{t.emailLabel}</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="contact__input"
                      placeholder={t.emailPlaceholder}
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="contact__form-group">
                    <label htmlFor="phone" className="contact__label">{t.phoneLabel}</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className="contact__input"
                      placeholder={t.phonePlaceholder}
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="contact__form-group">
                    <label htmlFor="message" className="contact__label">{t.messageLabel}</label>
                    <textarea
                      id="message"
                      name="message"
                      className="contact__textarea"
                      placeholder={t.messagePlaceholder}
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                    ></textarea>
                  </div>

                  <div className="contact__form-group">
                    <label className="contact__label">{t.attachFilesLabel}</label>
                    <div className="contact__upload-area" onClick={() => fileInputRef.current.click()}>
                      <AttachIcon />
                      <span className="contact__upload-btn">{t.attachFilesButton}</span>
                      <span className="contact__upload-hint">{t.attachFilesHint}</span>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".jpg,.jpeg,.png,.gif,.webp,.bmp,.svg,.pdf,.docx"
                      className="contact__file-input"
                      onChange={handleFileChange}
                    />
                    {fileErrors.length > 0 && (
                      <ul className="contact__file-errors">
                        {fileErrors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    )}
                    {attachedFiles.length > 0 && (
                      <ul className="contact__file-list">
                        {attachedFiles.map((file, i) => (
                          <li key={i} className="contact__file-item">
                            <span className="contact__file-name">{file.name}</span>
                            <span className="contact__file-size">{formatFileSize(file.size)}</span>
                            <button
                              type="button"
                              className="contact__file-remove"
                              onClick={() => removeFile(i)}
                              aria-label={t.removeFile}
                            >
                              <FaTimes />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="contact__form-submit">
                    <button
                      type="submit"
                      className="btn btn--primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (t.sending || 'Sending...') : t.sendMessage}
                    </button>
                  </div>
                </form>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default Contact;